/**
 * This is copied from `src/sections/global/containers/navigation.tsx` but limits the data
 * from redux that it reads to *only* state that's managed on the client. Components that
 * currently render the other implementation could use this, but they'll need to supply some additional
 * fields from "remote" state (global/section redux). Once that refactoring has happened, the other
 * file can be removed. Any changes made here should be synced to that file as well.
 */
import { useCallback, useMemo } from 'react';

import { connect, shallowEqual, useSelector } from 'react-redux';
import { useRouterActions } from 'react-resource-router';

import { CurrentDataContext } from 'src/app/current-data';
import { useAppData } from 'src/app/data';
import { IndexV4 } from 'src/app/index-v4';
import { useConsentBanner } from 'src/app/navigation-provider';
import { ComponentLinkProxy } from 'src/components/component-link';
import { ReposContext, ResizeEvent } from 'src/components/navigation';
import { AccountMenuCurrentUser } from 'src/components/navigation/src/components/account-menu';
import {
  NavigationNextListenerProps,
  NavigationNextStateProps,
  SearchTargetUserProps,
} from 'src/components/navigation/src/components/index-next';
import { IndexV3 } from 'src/components/navigation/src/components/index-v3';
import urls from 'src/components/navigation/src/urls';
import PullRequestLink from 'src/components/pull-request-link';
import RepositoryLink from 'src/components/repository-link';
import { WorkspaceLink } from 'src/components/workspace-link';
import { BANNER_HEIGHT } from 'src/constants/navigation';
import { useRecentData } from 'src/hooks/recent-data';
import { SEARCH_DRAWER_INIT } from 'src/redux/global/actions';
import search from 'src/redux/global/actions/search';
import toggleAtlassianSwitcher from 'src/redux/global/actions/toggle-atlassian-switcher';
import toggleCreateDrawer from 'src/redux/global/actions/toggle-create-drawer';
import toggleFeedbackButton from 'src/redux/global/actions/toggle-feedback-button';
import toggleKeyboardShortcutMenu from 'src/redux/global/actions/toggle-keyboard-shortcut-menu';
import toggleSearchDrawer from 'src/redux/global/actions/toggle-search-drawer';
import updateNavigationState from 'src/redux/global/actions/update-navigation-state';
import { getRouteFeaturesFromAppData } from 'src/router';
import toggleCloneDialog from 'src/sections/repository/actions/toggle-clone-dialog';
import {
  getIsMobileHeaderActive,
  getIsNavigationOpen,
  getIsSearchDrawerOpen,
  getIsSiteMessageActive,
  getIsOrgAdmin,
  getIsWorkspaceAdmin,
  getWorkspaceOrgId,
  getDefaultWorkspace,
} from 'src/selectors/global-selectors';
import { getCurrentProjectWorkspace } from 'src/selectors/project-selectors';
import {
  getCurrentRepository,
  getCurrentRepositoryWorkspace,
} from 'src/selectors/repository-selectors';
import { getCurrentUser, getTargetUser } from 'src/selectors/user-selectors';
import {
  getCurrentWorkspace,
  getRecentlyViewedWorkspacesKeys,
} from 'src/selectors/workspace-selectors';
import { settings } from 'src/settings';
import { BucketState, BucketDispatch } from 'src/types/state';
import globalUrls from 'src/urls';
import {
  publishScreenEvent,
  publishUiEvent,
  publishOperationalEvent,
  publishTrackEvent,
} from 'src/utils/analytics/publish';
import { getBbEnv } from 'src/utils/bb-env';

const mapDispatchToProps = (dispatch: BucketDispatch): DispatchProps => ({
  onCloneClick: () => {
    dispatch(toggleCreateDrawer(false));
    dispatch(toggleCloneDialog(true));
  },
  onCreateDrawerClose: () => dispatch(toggleCreateDrawer(false)),
  onCreateDrawerOpen: () => dispatch(toggleCreateDrawer(true)),
  onKeyboardShortcutsActivated: () =>
    dispatch(toggleKeyboardShortcutMenu(true)),
  onFeedbackModalOpen: () => dispatch(toggleFeedbackButton(true)),
  onResize: (event: ResizeEvent) =>
    dispatch(updateNavigationState(event.isOpen)),
  // @ts-ignore Property 'value' does not exist on type 'EventTarget & Element'
  onSearch: event => dispatch(search(event.target.value)),
  onSearchDrawerClose: () => dispatch(toggleSearchDrawer(false)),
  onSearchDrawerInit: () => dispatch({ type: SEARCH_DRAWER_INIT }),
  onSearchDrawerOpen: () => dispatch(toggleSearchDrawer(true)),
  onToggleAtlassianSwitcher: (isOpen: boolean) =>
    dispatch(toggleAtlassianSwitcher(isOpen)),
});

// A hook to gather data from redux that's common to each nav implementation
// TODO: Remove this once each section's nav uses the Navigation API and Resource
// to pass this data through (see: src/sections/workspace/components/navigation/navigation.tsx)
export const useLegacyReduxNavigationData = () => {
  const userPieces = useSelector((state: BucketState) => {
    const currentUser = getCurrentUser(state);
    // User pieces to avoid rerender thrash from denormalizing
    return {
      userEmail: state.global.currentUserEmail,
      userDisplayName: currentUser?.display_name,
      userAvatarUrl: currentUser?.links.avatar.href,
      userUuid: currentUser?.uuid,
      userWorkspaceId: currentUser?.extra?.workspace_id,
    };
  }, shallowEqual);

  const targetUserPieces = useSelector((state: BucketState) => {
    const targetUser = getTargetUser(state);
    // Target user pieces to avoid rerender thrash from denormalizing
    const pieces: SearchTargetUserProps = {
      targetUserDisplayName: targetUser?.display_name,
      targetUserUuid: targetUser?.uuid,
    };
    return pieces;
  }, shallowEqual);

  const defaultNavigationOpen = useSelector(getIsNavigationOpen);

  const globalBitbucketActions = useSelector(
    (state: BucketState) => state.global.bitbucketActions
  );

  const hasPride = useSelector(
    (state: BucketState) => state.global.features['bbc-pride-logo']
  );

  const horizontalNavigationItems = useSelector(
    (state: BucketState) => state.global.horizontalNavigationItems
  );

  const isBannerOpen = useSelector(getIsSiteMessageActive);
  const { consentBannerHeight } = useConsentBanner()[0];
  const topOffset = isBannerOpen
    ? BANNER_HEIGHT + consentBannerHeight
    : consentBannerHeight;

  const whatsNewUrl = useSelector(
    (state: BucketState) => state.global.whatsNewUrl
  );
  const attributionsUrl = '/site/attributions/';

  const adminHubUrl = settings().ADMINHUB_BASE_URL;
  const aaLoginUrl = settings().SOCIAL_AUTH_ATLASSIANID_LOGIN_PROMPT_URL;
  const aaLogoutUrl = settings().SOCIAL_AUTH_ATLASSIANID_LOGOUT_URL;
  const aaManageProfileUrl = settings().SOCIAL_AUTH_ATLASSIANID_PROFILE_URL;
  const canonUrl = settings().CANON_URL;

  const hasAtlassianAccount = useSelector((state: BucketState) => {
    const currentUser = getCurrentUser(state);
    return currentUser && currentUser.extra
      ? !!currentUser.extra.has_atlassian_account
      : false;
  });

  const switchAccountUrl = hasAtlassianAccount
    ? globalUrls.external.atlassianAccountLogin(
        aaLoginUrl,
        canonUrl,
        'select_account'
      )
    : undefined;

  const manageAccountUrl = hasAtlassianAccount ? aaManageProfileUrl : undefined;

  const logoutUrl = hasAtlassianAccount
    ? globalUrls.external.atlassianAccountLogout(aaLogoutUrl, canonUrl)
    : globalUrls.ui.logout();

  const currentWorkspace = useSelector(getCurrentWorkspace);
  const projectWorkspace = useSelector(getCurrentProjectWorkspace);
  const repoWorkspace = useSelector(getCurrentRepositoryWorkspace) || undefined;

  const currentRepository = useSelector(getCurrentRepository);

  return {
    defaultNavigationOpen,
    globalBitbucketActions,
    hasPride,
    horizontalNavigationItems,
    adminHubUrl,
    switchAccountUrl,
    manageAccountUrl,
    loginUrl: aaLoginUrl,
    logoutUrl,
    topOffset,
    attributionsUrl,
    whatsNewUrl,
    currentWorkspace: currentWorkspace || projectWorkspace || repoWorkspace,
    currentRepository,
    ...targetUserPieces,
    ...userPieces,
  };
};

const useClientNavigationState = () => {
  const appData = useAppData();
  const { isBitbucketLogoRefreshEnabled } =
    getRouteFeaturesFromAppData(appData);
  const isAtlassianSwitcherOpen = useSelector(
    (state: BucketState) => state.global.isAtlassianSwitcherOpen
  );
  const isCreateDrawerOpen = useSelector(
    (state: BucketState) => state.global.isCreateDrawerOpen
  );
  const isMobileHeaderActive = useSelector(getIsMobileHeaderActive);
  const isSearchDrawerOpen = useSelector(getIsSearchDrawerOpen);
  const recentlyViewedWorkspacesKeys = useSelector(
    getRecentlyViewedWorkspacesKeys
  );
  const searchQuery = useSelector(
    (state: BucketState) => state.global.searchQuery
  );

  const currentUser = useSelector(getCurrentUser);
  const environment = getBbEnv();

  const isOrgAdmin = useSelector(getIsOrgAdmin);
  const isWorkspaceAdmin = useSelector(getIsWorkspaceAdmin);
  const workspaceOrgId = useSelector(getWorkspaceOrgId);

  return {
    isBitbucketLogoRefreshEnabled,
    environment,
    isAtlassianSwitcherOpen,
    isCreateDrawerOpen,
    isMobileHeaderActive,
    isSearchDrawerOpen,
    isOrgAdmin,
    isWorkspaceAdmin,
    workspaceOrgId,
    recentlyViewedWorkspacesKeys,
    searchQuery,
    currentUser,
  };
};

type OptionalOverrideKeys =
  | 'defaultNavigationOpen'
  | 'isBeingRenderedInsideMobileNav';

type OptionalOverrides = Partial<
  Pick<NavigationNextStateProps, OptionalOverrideKeys>
>;

type DispatchProps = Omit<
  NavigationNextListenerProps,
  | 'publishUiEvent'
  | 'publishScreenEvent'
  | 'publishTrackEvent'
  | 'publishOperationalEvent'
>;

type StateProps = Omit<
  NavigationNextStateProps,
  OptionalOverrideKeys | keyof ReturnType<typeof useClientNavigationState>
> &
  OptionalOverrides;

type Props = DispatchProps & StateProps;

const NavContainer = (props: Props) => {
  const {
    defaultNavigationOpen,
    isBeingRenderedInsideMobileNav,
    containerName,
    containerHref,
    containerLogo,
    isPrivate,
    ...otherProps
  } = props;

  const { push } = useRouterActions();
  const { recentRepositories, loadRecentRepositories } = useRecentData();

  const onProductClick = useCallback(() => {
    push(urls.ui.root());
  }, [push]);

  const { currentUser, ...clientProps } = useClientNavigationState();

  const repoContextValue = useMemo(
    () => ({
      repositories: recentRepositories,
      loadRepositories: loadRecentRepositories,
    }),
    [recentRepositories, loadRecentRepositories]
  );

  const appData = useAppData();
  const { tenantId } = appData;
  const { isNavigationRestructureEnabled } =
    getRouteFeaturesFromAppData(appData);
  const defaultWorkspace = useSelector(getDefaultWorkspace);

  const Index = isNavigationRestructureEnabled ? IndexV4 : IndexV3;

  return (
    <ReposContext.Provider value={repoContextValue}>
      {/* Currently need to have useContainerNavigationProps for the v3 layout  */}
      <CurrentDataContext.Consumer>
        {currentData => (
          <Index
            defaultWorkspace={defaultWorkspace}
            defaultNavigationOpen={
              defaultNavigationOpen || clientProps.isMobileHeaderActive
            }
            isBeingRenderedInsideMobileNav={
              !!props.isBeingRenderedInsideMobileNav
            }
            linkComponent={ComponentLinkProxy}
            onProductClick={onProductClick}
            publishScreenEvent={publishScreenEvent}
            publishUiEvent={publishUiEvent}
            publishOperationalEvent={publishOperationalEvent}
            publishTrackEvent={publishTrackEvent}
            repositoryLinkComponent={RepositoryLink}
            pullRequestLinkComponent={PullRequestLink}
            workspaceLinkComponent={WorkspaceLink}
            currentUser={currentUser as AccountMenuCurrentUser}
            tenantId={tenantId}
            // This can't be undefined based on the props.useContainerNavigationProps !== undefined check above
            useContainerNavigationProps={props.useContainerNavigationProps!}
            // TODO: ADMINX-2750 - Figure out alternative solutions to render the correct
            // containerLogo, containerName, containerHref. Details in ticket
            containerLogo={
              containerLogo === ''
                ? ''
                : containerLogo ||
                  currentData.repository?.links.avatar.href ||
                  currentData.project?.links?.avatar.href ||
                  currentData.workspace?.links?.avatar.href
            }
            containerName={
              containerName === ''
                ? ''
                : containerName ||
                  currentData.repository?.name ||
                  currentData.project?.name ||
                  currentData.workspace?.name
            }
            containerHref={
              containerHref === ''
                ? ''
                : containerHref ||
                  currentData.repository?.links.self.href ||
                  currentData.project?.links?.self.href ||
                  currentData.workspace?.links?.self.href
            }
            isPrivate={
              isPrivate ||
              currentData.repository?.is_private ||
              currentData.project?.is_private ||
              currentData.workspace?.is_private
            }
            {...clientProps}
            {...otherProps}
          >
            {props.children}
          </Index>
        )}
      </CurrentDataContext.Consumer>
    </ReposContext.Provider>
  );
};

export default connect<{}, DispatchProps>(
  null,
  mapDispatchToProps
)(NavContainer);
